import React, { useState, useReducer, useContext, useEffect, memo, useCallback } from 'react'
import { cloneDeep } from 'lodash'
import { QAAddEditCategory, QAMemberLookup } from 'modules/Rest'
import CardHeader from './CardHeader'
import { Typography, TextField, Tooltip, Checkbox, FormControlLabel, Grid, Box, Chip, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Autocomplete } from '@mui/material';
import { MessageContext } from './Contexts'
import AButton from './AButton'
import ActionButtons from './ActionButtons'
import { setModes } from 'modules/Utils'
import { SimpleTableIcon } from './Icons'
import UserIcon from '@mui/icons-material/Person'
import UserGroupIcon from '@mui/icons-material/Group'
import { useSelectedDataRoom } from 'hooks/useSelectedDataRoom'

const groupMap = {
  DRUserType: 'User groups',
  DRDataRoom: 'Data room',
  DRUser: 'Users',
}

const findIndex = (pipeline, id) => pipeline.findIndex(({ identifier }) => identifier === id)

const reducer = (pipelineState, action) => {
  let newPipelineState

  switch (action.type) {
    case 'update':
      const index = findIndex(pipelineState, action.payload.identifier)
      newPipelineState = cloneDeep(pipelineState)
      newPipelineState[index] = {
        ...newPipelineState[index],
        ...action.payload,
      }
      return newPipelineState

    case 'addConfirmationStep':
      newPipelineState = cloneDeep(pipelineState)
      newPipelineState.splice(4, 0, {
        ...newPipelineState[3],
        identifier: 'confirmation_2',
        name: 'Confirmation 2',
      })
      newPipelineState[3].name = 'Confirmation 1'
      return newPipelineState

    case 'reset':
      return cloneDeep(action.payload)

    default:
      throw new Error()
  }
}

const useStyles = makeStyles(theme => ({
  tag: {
    marginRight: '100%',
  },
  showStep: {
    padding: '10px 0',
  },
  hideStep: {
    display: 'none',
  },
  members: {
    paddingTop: 10,
    [theme.breakpoints.up('md')]: {
      paddingRight: 40,
    },
    "& .MuiInputBase-root": {
      flexDirection: 'column',
      alignItems: 'flex-start',
    }
  },
  membersInput: {
    marginLeft: '3px',
    width: 'auto !important'
  },
  inlineIcon: {
    color: 'rgba(0, 0, 0, 0.5)',
    fontSize: 18,
    marginRight: 6,
    paddingTop: 2
  },
}))

const PipelineCheckbox = ({ tooltip, ...rest }) =>
      rest.checked === null ? null : (
        <Tooltip title={tooltip || ''}>
          <FormControlLabel {...rest} style={{ display: 'flex'}} />
        </Tooltip>
      )

export default memo(function QAPipelineCard({ category, refreshCategory }) {
  const classes = useStyles()
  const [pipelineState, dispatch] = useReducer(reducer, [])
  const [prevPipelineState, setPrevPipelineState] = useState('')
  const { messageDispatch } = useContext(MessageContext)
  const { dataRoom: data_room } = useSelectedDataRoom()
  const [availableMembers, setAvailableMembers] = useState([])

  const [mode, setMode] = useState('')
  const [showAutoWarning, setShowAutoWarning] = useState(false)
  const [pendingSetMode, setPendingSetMode] = useState(null)

  const getRelatedIcon = useCallback((kind) => {
    if (kind === 'DRUserType') {
      return <UserGroupIcon className={classes.inlineIcon}/>
    } else if (kind === 'DRUser') {
      return <UserIcon className={classes.inlineIcon}/>
    } else {
      return <SimpleTableIcon className={classes.inlineIcon}/>
    }
  }, [classes.inlineIcon])

  const formatMember = useCallback(
    (props, option, state, ownerState) => {
      const icon = getRelatedIcon(option.kind)
      const { key, ...optionProps } = props;
      return (
        <Box
          key={key}
          sx={{
            borderRadius: '8px',
            margin: '5px',
          }}
          component="li"
          {...optionProps}
        >
          {icon}{ownerState.getOptionLabel(option)}
        </Box>
      )

  }, [getRelatedIcon])


  useEffect(() => {
    dispatch({type: 'reset', payload: category.pipeline})
    setPrevPipelineState(category.pipeline)
  }, [category.pipeline])

  const doSavePipeline = async (setMode) => {
    try {
      setMode('pending')
      const { data: { success, info, ...data } } = await QAAddEditCategory({
        category: category.category,
        pipeline: pipelineState,
      })
      if (success) {
        dispatch({type: 'reset', payload: data.category.pipeline})
        setPrevPipelineState(data.category.pipeline)
        setModes(setMode, ['completing', 1500, ''])
      } else {
        throw new Error(info ? info : 'An error occurred')
      }
    } catch (error) {
      setMode('')
      messageDispatch({type: 'warning', payload: error.message})
    }
  }

  const savePipeline = async (e, setMode) => {
    const hasNewAuto = pipelineState.some((step) => {
      const prev = prevPipelineState.find(p => p.identifier === step.identifier)
      return step.auto && prev && !prev.auto
    })

    if (hasNewAuto) {
      setPendingSetMode(() => setMode)
      setShowAutoWarning(true)
    } else {
      doSavePipeline(setMode)
    }
  }

  useEffect(() => {
    try {
      async function getMembers() {
        const { data: { success, info, members } } = await QAMemberLookup({data_room: data_room.data_room})
        if (success) {
          setAvailableMembers(members)
        } else {
          setAvailableMembers([])
          throw new Error(info ? info : 'An error occurred')
        }
      }
      if (mode === 'editing' && data_room) {
        getMembers()
      }
    } catch (error) {
      messageDispatch({ type: 'warning', payload: error.message })
    }
  }, [mode, data_room, messageDispatch])

  return <>
    <CardHeader
      mode={mode}
      setMode={setMode}
      action={savePipeline}
      cancel={(e, setMode) => {
        dispatch({ type: 'reset', payload: prevPipelineState })
        setMode('')
      }}
      title='Pipeline'
      headingElement='h6'
    />

    <Grid container>
      {pipelineState.map((step, index) => (
        <React.Fragment key={step.identifier}>
          <Grid item sm={12} md={6} className={step.hidden ? classes.hideStep : classes.showStep}>
            <Typography variant='subtitle1'>Step {index}: {step.name}</Typography>

            <Autocomplete
              multiple
              options={availableMembers}
              renderOption={formatMember}
              groupBy={(option) => `${groupMap[option.kind]}`}
              value={step.members}
              getOptionLabel={(option) => option.display_name}
              classes={{ tag: classes.tag, root: classes.members, input: classes.membersInput }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const icon = getRelatedIcon(option.kind)
                  const { key, ...props } = getTagProps({ index })
                  return <Chip
                    key={option.member}
                    label={<>{icon}{option.display_name}</>}
                    {...props}
                  />
                })
              }
              renderInput={(params) => <TextField {...params} variant='outlined' label='Members' placeholder='Add...'/>}
              onChange={(_event, value) => {
                dispatch({
                  type: 'update',
                  payload: {
                    members: value,
                    identifier: step.identifier,
                  },
                })
              }}
              disabled={mode !== 'editing'}
              disableClearable={true}
              forcePopupIcon={false}
              filterSelectedOptions={true}
              isOptionEqualToValue={(option, value) => option.member === value.member
              }
            />
          </Grid>

          <Grid item sm={12} md={6} className={step.hidden ? classes.hideStep : classes.showStep}>
            <Typography variant='subtitle1'>Actions</Typography>

            {step.identifier !== 'answering' && (
              <PipelineCheckbox
                checked={step.auto}
                label='Automate step'
                disabled={mode !== 'editing'}
                tooltip='If set: the question is automatically moved forward to the next step in the pipeline'
                control={<Checkbox/>}
                onChange={() =>
                  dispatch({
                    type: 'update',
                    payload: {
                      auto: !step.auto,
                      identifier: step.identifier,
                    },
                  })
                }
              />
            )}
            <PipelineCheckbox checked={step.q_submit} label='Submit question' disabled={true} control={<Checkbox/>}/>
            <PipelineCheckbox checked={step.accept} label='Accept question' disabled={true} control={<Checkbox/>}/>
            <PipelineCheckbox checked={step.dismiss} label='Send back to submitter' disabled={true} control={<Checkbox/>}/>
            <PipelineCheckbox checked={step.a_submit} label='Answer question' disabled={true} control={<Checkbox/>}/>
            <PipelineCheckbox checked={step.confirm} label='Confirm answer' disabled={true} control={<Checkbox/>}/>
            <PipelineCheckbox checked={step.publish} label='Publish answer' disabled={true} control={<Checkbox/>}/>
            <PipelineCheckbox
              checked={step.undo}
              label='Undo last action'
              disabled={mode !== 'editing'}
              tooltip='If set: the user that performed the previous step can undo that action and send the question back to that step'
              control={<Checkbox/>}
              onChange={() =>
                dispatch({
                  type: 'update',
                  payload: {
                    undo: !step.undo,
                    identifier: step.identifier,
                  },
                })
              }
            />
            <PipelineCheckbox
              checked={step.prev_step}
              label='Send back to previous step'
              disabled={mode !== 'editing'}
              tooltip='If set: members can send the question back to the preceding step'
              control={<Checkbox/>}
              onChange={() =>
                dispatch({
                  type: 'update',
                  payload: {
                    prev_step: !step.prev_step,
                    identifier: step.identifier,
                  },
                })
              }
            />
            <PipelineCheckbox
              checked={step.assign_to}
              label='Assign question to users'
              disabled={mode !== 'editing'}
              tooltip='If set: members can select a specific user to carry out the next step in the pipeline'
              control={<Checkbox/>}
              onChange={() =>
                dispatch({
                  type: 'update',
                  payload: {
                    assign_to: !step.assign_to,
                    identifier: step.identifier,
                  },
                })
              }
            />

            <PipelineCheckbox
              checked={step.q_edit}
              label='Edit question'
              disabled={mode !== 'editing'}
              tooltip='If set: members can edit the question'
              control={<Checkbox/>}
              onChange={() =>
                dispatch({
                  type: 'update',
                  payload: {
                    q_edit: !step.q_edit,
                    identifier: step.identifier,
                  },
                })
              }
            />
            <PipelineCheckbox
              checked={step.a_edit}
              label='Edit answer'
              disabled={mode !== 'editing'}
              tooltip='If set: members can edit the answer'
              control={<Checkbox/>}
              onChange={() =>
                dispatch({
                  type: 'update',
                  payload: {
                    a_edit: !step.a_edit,
                    identifier: step.identifier,
                  },
                })
              }
            />
            <PipelineCheckbox
              checked={step.cat_edit}
              label='Edit category'
              disabled={mode !== 'editing'}
              tooltip='If set: members can switch the category of the question'
              control={<Checkbox/>}
              onChange={() =>
                dispatch({
                  type: 'update',
                  payload: {
                    cat_edit: !step.cat_edit,
                    identifier: step.identifier,
                  },
                })
              }
            />
            <PipelineCheckbox
              checked={step.prio_edit}
              label='Edit priority'
              disabled={mode !== 'editing'}
              tooltip='If set: members can edit the priority of the question'
              control={<Checkbox/>}
              onChange={() =>
                dispatch({
                  type: 'update',
                  payload: {
                    prio_edit: !step.prio_edit,
                    identifier: step.identifier,
                  },
                })
              }
            />
            <PipelineCheckbox
              checked={step.a_upload}
              label='Upload external files'
              disabled={mode !== 'editing'}
              tooltip='If set: members can upload external files to the answer'
              control={<Checkbox/>}
              onChange={() =>
                dispatch({
                  type: 'update',
                  payload: {
                    a_upload: !step.a_upload,
                    identifier: step.identifier,
                  },
                })
              }
            />
          </Grid>
        </React.Fragment>
      ))}
    </Grid>

    {mode === 'editing' && pipelineState[3].identifier === 'confirmation_1' && pipelineState[4].identifier !== 'confirmation_2' && (
      <AButton onClick={() => dispatch({ type: 'addConfirmationStep' })}>Add a second confirmation step</AButton>
    )}

    <Dialog open={showAutoWarning}>
      <DialogTitle>Confirmation request</DialogTitle>
      <DialogContent sx={{ width: 400 }}>
        <Typography variant='body2'>
          Enabling automation will trigger auto actions for all questions currently on the affected step(s).
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          When the changes to the pipeline are saved, questions will automatically move forward through the pipeline.
        </Typography>
      </DialogContent>
      <DialogActions>
        <ActionButtons
          mode='editing'
          setMode={() => {}}
          action={() => {
            setShowAutoWarning(false)
            if (pendingSetMode) doSavePipeline(pendingSetMode)
          }}
          cancel={() => {
            setShowAutoWarning(false)
            setPendingSetMode(null)
          }}
          actionTitle='Save'
          colorAction='error'
        />
      </DialogActions>
    </Dialog>
  </>
})
